<script setup lang="ts">
/**
 * Company details and commission rates.
 *
 * ── The company block is not decoration ──────────────────────────────────────
 *
 * These values are the letterhead on every invoice PDF (`InvoiceCompany` in
 * `core/admin/invoices/invoice-renderer.ts`) and the footer's registered-entity
 * line. An unset `company.registration` means invoices go out without a
 * registration number, which is exactly the sort of thing nobody notices until
 * an accountant does — so the fields that appear on a document say so.
 *
 * ── Bare keys, both ways ─────────────────────────────────────────────────────
 *
 * The fields here are `name`, `registration`, … without the `company.` prefix.
 * The API is asymmetric about it — the GET returns prefixed keys and the POST
 * adds the prefix itself — and `admin-api.ts` normalises both ends so this form
 * never has to know. Sending back what the GET returned would write
 * `company.company.name`: a silent no-op that leaves the form looking saved.
 *
 * ── Bulk save, not per-field ─────────────────────────────────────────────────
 *
 * The endpoint takes a `values` object, and a company address changes as a set:
 * saving the street without the postcode leaves a letterhead that is wrong in a
 * new way. One Save, one request, one consistent state.
 */
import { ref, watch } from 'vue';

import { adminApi } from './admin-api';
import { usePane } from './pane';

const company = usePane(() => adminApi.company());
const commissions = usePane(() => adminApi.commissions());

/** Local copies, so typing does not fight the fetched object. */
const companyDraft = ref<Record<string, string>>({});
const commissionDraft = ref<Record<string, string>>({});

watch(company.data, (v) => (companyDraft.value = { ...(v ?? {}) }), { immediate: true });
watch(commissions.data, (v) => (commissionDraft.value = { ...(v ?? {}) }), { immediate: true });

/**
 * The company fields, in letterhead order, with the ones that reach a document
 * marked. Driven off a table rather than the fetched keys so a field the
 * operator has never set still gets an input — an empty response would
 * otherwise render an empty form.
 */
const COMPANY_FIELDS: ReadonlyArray<{ key: string; label: string; onDocs?: boolean }> = [
  { key: 'name', label: 'Legal name', onDocs: true },
  { key: 'registration', label: 'Registration number', onDocs: true },
  { key: 'address', label: 'Address', onDocs: true },
  { key: 'country', label: 'Country', onDocs: true },
  { key: 'jurisdiction', label: 'Registered in', onDocs: true },
  { key: 'email', label: 'Contact email', onDocs: true },
  { key: 'domain', label: 'Domain' },
  { key: 'game_name', label: 'Game name' },
];

const COMMISSION_FIELDS: ReadonlyArray<{ key: string; label: string; hint: string }> = [
  { key: 'deposit_bps', label: 'Deposit', hint: 'basis points — 500 = 5%' },
  { key: 'purchase_bps', label: 'Purchase', hint: 'basis points' },
  { key: 'withdrawal_bps', label: 'Withdrawal', hint: 'basis points' },
  { key: 'min_withdrawal_cents', label: 'Minimum withdrawal', hint: 'in cents' },
];

const saved = ref<string | null>(null);
function flash(what: string): void {
  saved.value = what;
  window.setTimeout(() => (saved.value = null), 2500);
}
</script>

<template>
  <h1>Settings</h1>
  <p class="lede">Who the operator is, and what the house takes.</p>

  <p v-if="saved" class="note" data-test="admin-settings-saved">{{ saved }} saved.</p>

  <section>
    <h2>Company</h2>
    <p v-if="company.error.value" class="bad">{{ company.error.value }}</p>
    <div class="fields">
      <label v-for="f in COMPANY_FIELDS" :key="f.key" class="field">
        <span>{{ f.label }}<template v-if="f.onDocs"> · on invoices</template></span>
        <input v-model="companyDraft[f.key]" type="text" :data-test="`admin-${f.key}`" />
      </label>
    </div>
    <p class="note">
      The marked fields are the letterhead on every invoice PDF and the
      registered-entity line in the site footer.
    </p>
    <div class="toolbar" style="margin-top: 0.6rem">
      <button
        class="accent"
        :disabled="company.loading.value"
        data-test="admin-save-company"
        @click="company.act(() => adminApi.setCompany(companyDraft)).then(() => flash('Company'))"
      >
        Save company
      </button>
    </div>
  </section>

  <section>
    <h2>Commissions</h2>
    <p v-if="commissions.error.value" class="bad">{{ commissions.error.value }}</p>
    <div class="fields">
      <label v-for="f in COMMISSION_FIELDS" :key="f.key" class="field">
        <span>{{ f.label }} · {{ f.hint }}</span>
        <input v-model="commissionDraft[f.key]" type="text" inputmode="numeric" />
      </label>
    </div>
    <div class="toolbar" style="margin-top: 0.6rem">
      <button
        class="accent"
        :disabled="commissions.loading.value"
        @click="commissions.act(() => adminApi.setCommissions(commissionDraft)).then(() => flash('Commissions'))"
      >
        Save commissions
      </button>
    </div>
  </section>
</template>
